<template>
  <div class="macro-sidepanel px-4">
    <b-row class="d-flex align-items-center mb-2">
      <b-col class="d-flex align-items-center">
        <span class="title"><i
          class="fal fa-prescription-bottle mr-2"
          aria-hidden="true"
        />{{ sample.sampleLabel }}</span>
      </b-col>
      <b-col
        cols="auto"
        class="d-flex justify-content-end"
      >
        <div>
          <b-button
            v-shortkey="['shift', 'esc']"
            class="btn__icon btn__icon--primary p-0"
            @shortkey.prevent="closeMacroSidepanel"
            @click="closeMacroSidepanel"
          >
            <i
              class="far fa-times"
              aria-hidden="true"
            />
          </b-button>
        </div>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <description-editor
          v-if="sample && attachmentTypeIdForMacroAudio"
          :key="sample.id"
          ref="descriptionEditor"
          :disabled="!$_isCaseEditable(sample.caseStudy && sample.caseStudy.status.code)"
          class="macroscopy-citology"
          name="macroscopy-citology"
          :laboratory-id="labId"
          :description-title="$t('laboratory.content.macroscopy')"
          :description-service="sampleMacroService"
          :parent-entity-ids-array="[labId, sample.caseStudy.casId, sample.id]"
          :root-filename-for-audio-description="`${sample.samSampleNumber}-MacroAudio-`"
          :attachment-type-id-for-description-audio="attachmentTypeIdForMacroAudio"
          :attachment-service-for-audio-attachments="sampleAttachmentService"
          :case-id="sample.caseStudy.casId"
          :sample-id="sample.id"
          text-template-modal-ui-component-code="uicomponent.laboratory.templateconfig"
          :attachment-service="sampleAttachmentService"
          :hide-attach-operations="!$_canAttachDocumentToSample()"
          :disable-attach-operations="!$_canBeDocumentAttachedToSample(sample)"
          :hide-edit-operations="!$_canEditSample()"
        />
      </b-col>
    </b-row>
    <b-row class="mt-3">
      <b-col cols="auto">
        <div class="form-label">
          <label>{{ $t('grossing.content.partial') }}</label>
        </div>
        <div class="d-flex">
          <div class="switch-btn p-0 d-flex align-items-center">
            <input
              id="togglePartialEmbedding"
              v-model="partialEmbedding"
              :disabled="!$_isCaseEditable(sample && sample.caseStudy && sample.caseStudy.status.code)"
              class="offscreen"
              type="checkbox"
              aria-hidden="true"
            >
            <label
              for="togglePartialEmbedding"
              class="switch m-0"
            />
          </div>
        </div>
      </b-col>
    </b-row>
    <b-row class="mt-3">
      <b-col>
        <hr>
      </b-col>
    </b-row>
    <b-row>
      <b-col class="d-flex justify-content-end">
        <b-button
          v-if="$_canEditSample()"
          :disabled="!$_isCaseEditable(sample && sample.caseStudy && sample.caseStudy.status.code)"
          size="sm"
          class="btn--primary ml-5"
          @click="save"
        >
          <i
            class="far fa-save mr-2"
            aria-hidden="true"
          />
          {{ $t('global.save') }}
        </b-button>
      </b-col>
    </b-row>
    <samples-update-util ref="samplesUpdate" />
  </div>
</template>

<script>
import DescriptionEditor from '@/components/description/DescriptionEditor'
import sampleMacroService from '@/api/resources/description/SampleMacroService'
import attachmentTypeService from '@/api/resources/attachment/attachmentTypeService'
import sampleAttachmentService from '@/api/resources/attachment/sampleAttachmentService'
import laboratorySampleServices from '@/modules/laboratoryModule/services/laboratorySampleServices'
import SamplesUpdateUtil from '@/components/common/SamplesUpdateUtil'
import { SampleOperationGuardMixin } from '@/api/operations/mixins/guards/sample-operation-guard.mixin'
import { CaseOperationGuardMixin } from '@/api/operations/mixins/guards/case-operation-guard.mixin'

export default {
    name: 'MacroSidepanel',
    components: {
        DescriptionEditor,
        SamplesUpdateUtil
    },
    mixins: [
        SampleOperationGuardMixin,
        CaseOperationGuardMixin
    ],
    props: {
        labId: { type: Number },
        sample: { type: Object }
    },
    data() {
        return {
            attachmentTypeIdForMacroAudio: null,
            partialEmbedding: null,
            originalConcurrencyUpdatedAt: null,
            originalPartialEmbedding: null
        }
    },
    computed: {
        sampleMacroService() {
            return sampleMacroService
        },
        sampleAttachmentService() {
            return sampleAttachmentService
        }
    },
    watch: {
        sample() {
            this.$refs.descriptionEditor.loadData()
            this.loadSampleInformation()
        }
    },
    created() {
        this.loadAttachmentTypeIdForMacroAudio()
        this.loadSampleInformation()
    },
    methods: {
        async save() {
            const showSuccessToast = false
            const sampleSaved = this.partialEmbedding !== this.originalPartialEmbedding ? await this.changePartialEmbedding() : null
            const macroSaved = this.$refs.descriptionEditor.hasBeenChanged() ? await this.$refs.descriptionEditor.save(showSuccessToast) : null
            if (sampleSaved || macroSaved) {
                this.showToastSuccessGlobal()
            }
            if (sampleSaved) {
                this.mapSampleData(sampleSaved)
            }
        },
        async changePartialEmbedding() {
            const sampleData = {
                samPartialEmbedding: this.partialEmbedding,
                originalConcurrencyUpdatedAt: this.originalConcurrencyUpdatedAt
            }
            return this.$refs.samplesUpdate.updateSample(this.labId, this.sample.caseStudy.casId, this.sample.id, sampleData, laboratorySampleServices)
        },
        async loadSampleInformation() {
            const [err, response] = await this.toCall(laboratorySampleServices.getSampleById(this.labId, this.sample.caseStudy.casId, this.sample.id))
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.mapSampleData(response)
        },
        mapSampleData(sampleData) {
            this.originalConcurrencyUpdatedAt = sampleData.originalConcurrencyUpdatedAt
            this.partialEmbedding = sampleData.samPartialEmbedding
            this.originalPartialEmbedding = sampleData.samPartialEmbedding
        },
        closeMacroSidepanel() {
            this.$emit('closeMacroSidepanel', false)
        },
        showSynopticModal() {
            this.$emit('showSynopticModal')
        },
        showTextModal() {
            this.$emit('showTextModal')
        },
        async loadAttachmentTypeIdForMacroAudio() {
            const [error, response] = await this.toCall(attachmentTypeService.getAttachmentType('SAMPLE', 'attachment.type.macroexaminationaudio.code'))
            if (error) {
                this.showErrorModalGlobal()
                return
            }
            this.attachmentTypeIdForMacroAudio = response.atyId
        }
    }
}
</script>

<style lang="scss">

.macroscopy-citology .ql-editor {
  height: calc(100vh - 346px);
  overflow-y: auto;
}

.macroscopy-citology .title {
  color: vt-color(primary);
}

</style>

<style lang="scss" scoped>

.switch-label {
  font-size: font-size(very-small);
  color: vt-color(dark-text);
}

.switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 20px;
  background-color: rgba(0, 0, 0, 0.25);
  border-radius: 20px;
  transition: all 0.3s;
  cursor: pointer;
}

.switch::after {
  content: "";
  position: absolute;
  width: 18px;
  height: 18px;
  border-radius: 18px;
  background-color: white;
  top: 1px;
  left: 1px;
  transition: all 0.3s;
}

input[type="checkbox"]:checked + .switch::after {
  transform: translateX(20px);
}

input[type="checkbox"]:checked + .switch {
  background-color: vt-color(primary);
}

.offscreen {
  position: absolute;
  left: -9999px;
}

.macro-sidepanel .title {
  font-size: font-size(big);
  color: vt-color(primary);
}

@media (max-width: 1300px) {
  .macroscopy-citology .ql-editor {
    height: calc(100vh - 336px);
    overflow-y: auto;
  }
  .macro-sidepanel .title {
    font-size: font-size(normal);
    color: vt-color(primary);
  }
}
</style>
